import * as React from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { AlarmClock, Download, Inbox, Lock, RefreshCw } from "lucide-react";
import { toast } from "sonner";

import { EmptyState } from "@/components/empty-state";
import { SectionHeading } from "@/components/section-heading";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { listRetryQueue, retryNoticeNow, runRetrySweepNow } from "@/lib/email-admin.functions";

export const Route = createFileRoute("/_authenticated/admin/email-retries")({
  head: () => ({
    meta: [
      { title: "Email retry queue — Quibblies" },
      {
        name: "description",
        content:
          "Staff dashboard for the scheduled email retry sweep: failed notices, attempts, last error and the next scheduled retry.",
      },
      { property: "og:title", content: "Email retry queue — Quibblies" },
      { property: "og:description", content: "Monitor failed account notices and their retries." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: EmailRetriesPage,
});

function relativeTime(value: string | null) {
  if (!value) return "—";
  const diff = Date.parse(value) - Date.now();
  const minutes = Math.round(diff / 60000);
  if (Number.isNaN(minutes)) return "—";
  if (Math.abs(minutes) < 1) return "now";
  if (minutes > 0)
    return minutes < 60 ? `in ${minutes} min` : `in ${Math.round(minutes / 60)} hr`;
  const ago = Math.abs(minutes);
  return ago < 60 ? `${ago} min ago` : `${Math.round(ago / 60)} hr ago`;
}

function EmailRetriesPage() {
  const queryClient = useQueryClient();
  const load = useServerFn(listRetryQueue);
  const sweep = useServerFn(runRetrySweepNow);
  const retryOne = useServerFn(retryNoticeNow);

  const { data, isLoading, error } = useQuery({
    queryKey: ["email-retry-queue"],
    queryFn: () => load(),
    retry: false,
    refetchInterval: 30_000,
  });

  const invalidate = () =>
    void queryClient.invalidateQueries({ queryKey: ["email-retry-queue"] });

  const runSweep = useMutation({
    mutationFn: () => sweep(),
    onSuccess: (result) => {
      toast.success(`Sweep finished — ${result?.processed ?? 0} notice(s) re-attempted.`);
      invalidate();
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const retryRow = useMutation({
    mutationFn: (noticeId: string) => retryOne({ data: { noticeId } }),
    onSuccess: (result) => {
      toast[result.status === "sent" ? "success" : "error"](
        result.status === "sent" ? "Delivered." : `Still failing: ${result.error ?? result.status}`,
      );
      invalidate();
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const [search, setSearch] = React.useState("");
  const [template, setTemplate] = React.useState("all");
  const [status, setStatus] = React.useState("all");

  const allRows = React.useMemo(() => data?.rows ?? [], [data]);
  const templates = React.useMemo(
    () => Array.from(new Set(allRows.map((row) => row.template))).sort(),
    [allRows],
  );

  const rows = React.useMemo(() => {
    const term = search.trim().toLowerCase();
    return allRows.filter((row) => {
      if (term && !row.email.toLowerCase().includes(term)) return false;
      if (template !== "all" && row.template !== template) return false;
      if (status === "exhausted" && !row.exhausted) return false;
      if (status === "due" && !(row.nextRetryAt && Date.parse(row.nextRetryAt) <= Date.now()))
        return false;
      if (status === "waiting" && !(row.nextRetryAt && Date.parse(row.nextRetryAt) > Date.now()))
        return false;
      if (
        status !== "all" &&
        status !== "exhausted" &&
        status !== "due" &&
        status !== "waiting" &&
        row.deliveryStatus !== status
      )
        return false;
      return true;
    });
  }, [allRows, search, status, template]);

  type SortKey = "attempts" | "nextRetryAt" | "lastAttemptAt" | "createdAt";
  const [sortKey, setSortKey] = React.useState<SortKey>("nextRetryAt");
  const [sortDir, setSortDir] = React.useState<"asc" | "desc">("asc");
  const [page, setPage] = React.useState(1);
  const pageSize = 25;

  function toggleSort(key: SortKey) {
    if (key === sortKey) setSortDir((dir) => (dir === "asc" ? "desc" : "asc"));
    else {
      setSortKey(key);
      setSortDir(key === "attempts" ? "desc" : "asc");
    }
    setPage(1);
  }

  const sorted = React.useMemo(() => {
    const value = (row: (typeof rows)[number]) => {
      if (sortKey === "attempts") return row.attempts;
      const raw = row[sortKey];
      // Rows with nothing scheduled sort last, whichever direction is chosen.
      return raw ? Date.parse(raw) : sortDir === "asc" ? Number.MAX_SAFE_INTEGER : -1;
    };
    return [...rows].sort((a, b) =>
      sortDir === "asc" ? value(a) - value(b) : value(b) - value(a),
    );
  }, [rows, sortDir, sortKey]);

  const pageCount = Math.max(1, Math.ceil(sorted.length / pageSize));
  const currentPage = Math.min(page, pageCount);
  const paged = sorted.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  React.useEffect(() => {
    setPage(1);
  }, [search, status, template]);

  /** Exports exactly what the filters currently show, not the whole queue. */
  function exportCsv() {
    const header = [
      "notice_id",
      "template",
      "subject",
      "recipient",
      "delivery_status",
      "attempts",
      "max_attempts",
      "failure_reason",
      "delivery_error",
      "last_attempt_at",
      "next_retry_at",
      "created_at",
    ];
    const escape = (value: unknown) => `"${String(value ?? "").replace(/"/g, '""')}"`;
    const csv = [
      header.join(","),
      ...rows.map((row) =>
        [
          row.id,
          row.template,
          row.subject,
          row.email,
          row.deliveryStatus,
          row.attempts,
          row.maxAttempts,
          row.failureReason,
          row.deliveryError,
          row.lastAttemptAt,
          row.nextRetryAt,
          row.createdAt,
        ]
          .map(escape)
          .join(","),
      ),
    ].join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `email-retry-queue-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${rows.length} notice(s).`);
  }

  const summary = data?.summary;

  return (
    <div className="mx-auto max-w-6xl px-4 py-12 sm:px-6">
      <SectionHeading
        title="Email retry queue"
        description="Every notice the scheduled sweep is still working through, with its attempt count, last error and next scheduled retry. The sweep runs automatically every 10 minutes."
      />

      {isLoading ? (
        <Skeleton className="mt-8 h-64 w-full rounded-2xl" />
      ) : error ? (
        <div className="mt-8">
          <EmptyState
            icon={Lock}
            title="Administrator access required"
            description="Only administrators and the Owner can see email delivery internals."
          />
        </div>
      ) : (
        <div className="mt-8 space-y-8">
          <div className="flex flex-wrap items-center gap-4">
            {[
              { label: "In queue", value: summary?.total ?? 0 },
              { label: "Due now", value: summary?.due ?? 0 },
              { label: "Waiting on backoff", value: summary?.waiting ?? 0 },
              { label: "Retries exhausted", value: summary?.exhausted ?? 0 },
            ].map((stat) => (
              <div
                key={stat.label}
                className="min-w-[9rem] flex-1 rounded-2xl border border-border/60 bg-surface/50 p-4"
              >
                <p className="text-xs uppercase tracking-wide text-muted-foreground">
                  {stat.label}
                </p>
                <p className="mt-1 font-display text-2xl text-foreground">{stat.value}</p>
              </div>
            ))}
            <Button
              variant="soft"
              size="pill"
              disabled={runSweep.isPending}
              onClick={() => runSweep.mutate()}
            >
              <RefreshCw className="h-4 w-4" aria-hidden="true" />
              {runSweep.isPending ? "Sweeping…" : "Run sweep now"}
            </Button>
          </div>

          <div className="flex flex-wrap items-end gap-4 rounded-2xl border border-border/60 bg-surface/40 p-4">
            <div className="min-w-[14rem] flex-1">
              <Label htmlFor="retry-search">Search by email</Label>
              <Input
                id="retry-search"
                value={search}
                placeholder="reader@example.com"
                onChange={(event) => setSearch(event.target.value)}
              />
            </div>
            <div className="min-w-[11rem]">
              <Label htmlFor="retry-template">Notice type</Label>
              <select
                id="retry-template"
                value={template}
                onChange={(event) => setTemplate(event.target.value)}
                className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
              >
                <option value="all">All types</option>
                {templates.map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </select>
            </div>
            <div className="min-w-[11rem]">
              <Label htmlFor="retry-status">Status</Label>
              <select
                id="retry-status"
                value={status}
                onChange={(event) => setStatus(event.target.value)}
                className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
              >
                <option value="all">All statuses</option>
                <option value="due">Due now</option>
                <option value="waiting">Waiting on backoff</option>
                <option value="exhausted">Retries exhausted</option>
              </select>
            </div>
            <Button variant="soft" size="pill" disabled={rows.length === 0} onClick={exportCsv}>
              <Download className="h-4 w-4" aria-hidden="true" />
              Export CSV
            </Button>
          </div>

          {rows.length === 0 ? (
            <EmptyState
              icon={Inbox}
              title={allRows.length === 0 ? "Nothing waiting" : "No matches"}
              description={
                allRows.length === 0
                  ? "No notice has failed delivery. Anything that does will appear here with its retry schedule."
                  : "No queued notice matches those filters. Try widening the search."
              }
            />
          ) : (
            <div className="overflow-x-auto rounded-2xl border border-border/60">
              <table className="w-full text-left text-sm">
                <thead className="bg-surface/60 text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="px-4 py-3">Notice</th>
                    <th className="px-4 py-3">Recipient</th>
                    <SortableHeader
                      label="Attempts"
                      active={sortKey === "attempts"}
                      direction={sortDir}
                      onClick={() => toggleSort("attempts")}
                    />
                    <th className="px-4 py-3">Last error</th>
                    <SortableHeader
                      label="Last attempt"
                      active={sortKey === "lastAttemptAt"}
                      direction={sortDir}
                      onClick={() => toggleSort("lastAttemptAt")}
                    />
                    <SortableHeader
                      label="Next retry"
                      active={sortKey === "nextRetryAt"}
                      direction={sortDir}
                      onClick={() => toggleSort("nextRetryAt")}
                    />
                    <th className="px-4 py-3" />
                  </tr>
                </thead>
                <tbody>
                  {paged.map((row) => (
                    <tr key={row.id} className="border-t border-border/50 align-top">
                      <td className="px-4 py-3">
                        <span className="text-foreground">{row.template}</span>
                        <span className="mt-0.5 block text-xs text-muted-foreground">
                          {row.subject}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">{row.email}</td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {row.attempts}/{row.maxAttempts}
                        {row.exhausted ? (
                          <span className="mt-0.5 block text-xs text-destructive">
                            retries exhausted
                          </span>
                        ) : null}
                      </td>
                      <td className="max-w-[18rem] px-4 py-3 text-xs text-muted-foreground">
                        {row.failureReason ?? row.deliveryStatus}
                        {row.deliveryError ? (
                          <span className="mt-0.5 block break-words">{row.deliveryError}</span>
                        ) : null}
                      </td>
                      <td className="px-4 py-3 text-xs text-muted-foreground">
                        {relativeTime(row.lastAttemptAt)}
                      </td>
                      <td className="px-4 py-3 text-xs text-muted-foreground">
                        {row.nextRetryAt ? (
                          <span className="inline-flex items-center gap-1">
                            <AlarmClock className="h-3.5 w-3.5" aria-hidden="true" />
                            {relativeTime(row.nextRetryAt)}
                          </span>
                        ) : (
                          "not scheduled"
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Button
                          size="sm"
                          variant="soft"
                          disabled={retryRow.isPending}
                          onClick={() => retryRow.mutate(row.id)}
                        >
                          Retry now
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {sorted.length > pageSize ? (
            <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-muted-foreground">
              <p>
                Showing {(currentPage - 1) * pageSize + 1}–
                {Math.min(currentPage * pageSize, sorted.length)} of {sorted.length}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="soft"
                  size="sm"
                  disabled={currentPage === 1}
                  onClick={() => setPage(currentPage - 1)}
                >
                  Previous
                </Button>
                <span>
                  Page {currentPage} of {pageCount}
                </span>
                <Button
                  variant="soft"
                  size="sm"
                  disabled={currentPage === pageCount}
                  onClick={() => setPage(currentPage + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}

/** A column header that sorts the queue, with the current direction announced. */
function SortableHeader({
  label,
  active,
  direction,
  onClick,
}: {
  label: string;
  active: boolean;
  direction: "asc" | "desc";
  onClick: () => void;
}) {
  return (
    <th className="px-4 py-3" aria-sort={active ? (direction === "asc" ? "ascending" : "descending") : "none"}>
      <button
        type="button"
        onClick={onClick}
        className="inline-flex items-center gap-1 uppercase tracking-wide transition-smooth hover:text-foreground"
      >
        {label}
        <span aria-hidden="true">{active ? (direction === "asc" ? "\u2191" : "\u2193") : "\u2195"}</span>
      </button>
    </th>
  );
}
